import { LanguagesEnum } from '@/api/enums';
import { AnswerOptionType, ModifiedAnswerOption } from '@/client/quizzes';
import { SecurityProfileQuiz } from '@/client/security-profile';
import { useSubmitSecurityProfileResponses } from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { getTranslatedContent } from '@/utils/helpers';
import { modifyCourseQuiz } from '@/utils/helpers/courses.helper';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnboardingQuestion } from './OnboardingQuestion';
import { OnboardingSuccess } from './OnboardingSuccess';

type OnboardingStep = 'questions' | 'success';

interface QuestionResponse {
  selectedAnswers: {
    id: string;
    text: string;
    isCorrect: boolean;
    type: AnswerOptionType;
  }[];
  textResponse?: string;
}

interface SecurityProfileOnboardingProps {
  quiz: SecurityProfileQuiz;
  accountId: string;
  attemptLanguage: LanguagesEnum;
  userName?: string;
  onSubmitted: () => void;
  onComplete: () => void;
}

export const SecurityProfileOnboarding: React.FC<
  SecurityProfileOnboardingProps
> = ({
  quiz,
  accountId,
  attemptLanguage,
  userName,
  onSubmitted,
  onComplete,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { mutateAsync: submitResponses } =
    useSubmitSecurityProfileResponses(accountId);
  const [step, setStep] = useState<OnboardingStep>('questions');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, QuestionResponse>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const adaptedQuiz = useMemo(() => {
    if (!quiz) return null;
    return modifyCourseQuiz(quiz);
  }, [quiz]);

  const totalQuestions = adaptedQuiz?.questions?.length || 0;

  const questionOrder = useMemo(
    () => Array.from({ length: totalQuestions }, (_, i) => i),
    [totalQuestions],
  );

  const isLastQuestion = currentQuestionIndex + 1 >= totalQuestions;

  const handleSkip = useCallback(() => {
    onComplete();
  }, [onComplete]);

  const handleOptionSelect = useCallback(
    (answerOption: ModifiedAnswerOption, checked: boolean) => {
      const reorderedIndex = questionOrder[currentQuestionIndex];
      const currentQuestion = adaptedQuiz?.questions?.[reorderedIndex];
      const questionId = currentQuestion?.id;
      if (!questionId) return;

      const isMultiChoice = currentQuestion?.type === 'multiple-choice';

      const option = {
        id: answerOption.id || '',
        text: getTranslatedContent(answerOption, attemptLanguage),
        isCorrect: !!answerOption.isCorrect,
        type: answerOption.type,
      };

      setResponses((prev) => {
        const current = prev[questionId] || {
          selectedAnswers: [],
          textResponse: undefined,
        };

        return {
          ...prev,
          [questionId]: {
            ...current,
            selectedAnswers: isMultiChoice
              ? checked
                ? [...current.selectedAnswers, option]
                : current.selectedAnswers.filter((opt) => opt.id !== option.id)
              : checked
              ? [option]
              : [],
          },
        };
      });
    },
    [adaptedQuiz, currentQuestionIndex, attemptLanguage, questionOrder],
  );

  const handleTextChange = useCallback(
    (value: string) => {
      const reorderedIndex = questionOrder[currentQuestionIndex];
      const currentQuestion = adaptedQuiz?.questions?.[reorderedIndex];
      const questionId = currentQuestion?.id;
      if (!questionId) return;

      setResponses((prev) => ({
        ...prev,
        [questionId]: {
          ...(prev[questionId] || { selectedAnswers: [] }),
          textResponse: value,
        },
      }));
    },
    [adaptedQuiz, currentQuestionIndex, questionOrder],
  );

  const validateCurrentQuestion = useCallback((): boolean => {
    const reorderedIndex = questionOrder[currentQuestionIndex];
    const currentQuestion = adaptedQuiz?.questions?.[reorderedIndex];
    const questionId = currentQuestion?.id;
    if (!questionId) return false;

    const response = responses[questionId] || {
      selectedAnswers: [],
      textResponse: undefined,
    };

    const hasCustomInput = response.selectedAnswers.some(
      (opt) => opt.type === AnswerOptionType.CUSTOM_INPUT,
    );

    if (hasCustomInput && !response.textResponse?.trim()) {
      toast?.error(t('securityProfile.error.customInputRequired'), '');
      return false;
    }

    return true;
  }, [adaptedQuiz, currentQuestionIndex, responses, toast, t, questionOrder]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const submissionData = (adaptedQuiz?.questions || []).map((question) => {
        const questionId = question.id || '';
        const response = responses[questionId] || {
          selectedAnswers: [],
          textResponse: undefined,
        };

        const hasCustomInput = response.selectedAnswers.some(
          (opt) => opt.type === AnswerOptionType.CUSTOM_INPUT,
        );

        return {
          questionId,
          selectedAnswerIds: response.selectedAnswers.map((opt) => opt.id),
          textResponse: hasCustomInput ? response.textResponse : undefined,
        };
      });

      await submitResponses(submissionData);
      onSubmitted();
      setStep('success');
    } catch {
      toast?.error(t('securityProfile.error.submitFailed'), '');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    adaptedQuiz,
    responses,
    submitResponses,
    onSubmitted,
    toast,
    t,
  ]);

  const handleNext = useCallback(async () => {
    if (!validateCurrentQuestion()) return;

    if (isLastQuestion) {
      await handleSubmit();
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  }, [validateCurrentQuestion, isLastQuestion, handleSubmit]);

  if (step === 'questions') {
    return (
      <OnboardingQuestion
        quiz={quiz}
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={totalQuestions}
        responses={responses}
        onOptionSelect={handleOptionSelect}
        onTextChange={handleTextChange}
        onNext={handleNext}
        onSkip={handleSkip}
        isSubmitting={isSubmitting}
        isLastQuestion={isLastQuestion}
        attemptLanguage={attemptLanguage}
        userName={userName}
        questionOrder={questionOrder}
      />
    );
  }

  return <OnboardingSuccess onContinue={onComplete} />;
};
